import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const NON_PAID = ['issued', 'pending_check', 'insured', 'overdue', 'disputed', 'claimed'];

const STATUS_LABELS: Record<string, string> = {
  draft: 'Concept', issued: 'Verzonden', pending_check: 'Toetsing loopt',
  insured: 'Verzekerd', paid: 'Betaald', claimed: 'Geclaimd',
  overdue: 'Vervallen', disputed: 'Betwist', archived: 'Gearchiveerd',
};
const STATUS_COLOR: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-700', issued: 'bg-mfb-blue/10 text-mfb-blue',
  pending_check: 'bg-amber-100 text-amber-700', insured: 'bg-emerald-100 text-emerald-700',
  paid: 'bg-emerald-200 text-emerald-800', claimed: 'bg-red-100 text-red-700',
  overdue: 'bg-red-100 text-red-700', disputed: 'bg-orange-100 text-orange-700',
};
const gradeColor = (grade: number | null): string => {
  if (grade == null) return 'bg-slate-100 text-slate-500';
  if (grade <= 2) return 'bg-emerald-100 text-emerald-800';
  if (grade === 3) return 'bg-mfb-blue/10 text-mfb-blue';
  if (grade === 4) return 'bg-amber-100 text-amber-800';
  if (grade === 5) return 'bg-orange-100 text-orange-800';
  if (grade === 6) return 'bg-red-100 text-red-800';
  return 'bg-red-200 text-red-900';
};

const fmt = (n: number) =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n || 0);

export const DebtorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();

  const [debtor, setDebtor] = useState<any>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  const headers = { Authorization: `Bearer ${accessToken}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, inv] = await Promise.all([
        fetch(`${API_BASE_URL}/api/debtors/${id}`, { headers }).then((r) => r.json()),
        fetch(`${API_BASE_URL}/api/invoices`, { headers }).then((r) => r.json()),
      ]);
      setDebtor(d);
      setInvoices((inv.invoices || []).filter((i: any) => i.debtor_id === id));
      setError('');
    } catch {
      setError('Gegevens laden mislukt');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, accessToken]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    load();
  }, [isAuthenticated, accessToken, navigate, load]);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const runCreditCheck = async () => {
    setBusy('check');
    try {
      await fetch(`${API_BASE_URL}/api/credit-checks`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ debtor_id: id }),
      });
      await load();
      showToast('Kredietcheck uitgevoerd');
    } catch { setError('Kredietcheck mislukt'); } finally { setBusy(''); }
  };

  const fileClaim = async (invoice: any) => {
    setBusy(invoice.id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/claims`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_id: invoice.id, claim_amount: invoice.coverage_amount || invoice.amount_gross }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Mislukt'); }
      await load();
      showToast(`Schade gemeld voor ${invoice.invoice_number}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Schade melden mislukt'); } finally { setBusy(''); }
  };

  const offerCollection = (invoice: any) => {
    showToast(`${invoice.invoice_number} aangeboden ter incasso`);
  };

  if (loading) return <Layout><div className="text-center py-12 text-slate-500">Laden...</div></Layout>;
  if (!debtor || debtor.detail) return <Layout><div className="text-center py-12 text-slate-500">Debiteur niet gevonden.</div></Layout>;

  const grade: number | null = debtor.grade ?? null;
  const maxDekking: number = debtor.dekking ?? 0;
  const insurable: boolean = debtor.verzekerbaar ?? (maxDekking > 0);

  const openstaand = invoices.filter((i) => NON_PAID.includes(i.status)).reduce((s, i) => s + Number(i.amount_gross || 0), 0);
  const verzekerd = invoices.filter((i) => i.status === 'insured').reduce((s, i) => s + Number(i.coverage_amount || 0), 0);
  const betaald = invoices.filter((i) => i.status === 'paid').reduce((s, i) => s + Number(i.amount_gross || 0), 0);

  return (
    <Layout>
      {/* Back + toast */}
      <button onClick={() => navigate('/debtors')} className="text-sm text-mfb-blue hover:underline mb-4">
        ← Terug naar debiteuren
      </button>
      {toast && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">{toast}</div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {/* Header card */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-bold ${gradeColor(grade)}`}>
              {grade ?? '—'}
            </div>
            <div>
              <h1 className="font-display text-2xl font-semibold text-mfb-blue">{debtor.company_name}</h1>
              <p className="text-sm text-slate-500">
                KvK {debtor.company_number || '—'} · {debtor.country_code}
                {' · '}
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${insurable ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                  {insurable ? 'Verzekerbaar' : 'Niet verzekerbaar'}
                </span>
              </p>
            </div>
          </div>
          <button
            onClick={runCreditCheck}
            disabled={busy === 'check'}
            className="px-4 py-2 bg-mfb-orange hover:bg-mfb-orange-dark disabled:bg-slate-400 text-white rounded-lg text-sm font-semibold transition shadow-sm whitespace-nowrap"
          >
            {busy === 'check' ? 'Bezig...' : '🔍 Kredietcheck uitvoeren'}
          </button>
        </div>
      </div>

      {/* Saldo tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl p-5 text-white bg-gradient-to-br from-orange-500 to-mfb-orange">
          <p className="text-xs uppercase tracking-wide text-white/80">Openstaand</p>
          <p className="text-2xl font-bold mt-1">{fmt(openstaand)}</p>
        </div>
        <div className="rounded-2xl p-5 text-white bg-gradient-to-br from-mfb-blue to-mfb-blue-light">
          <p className="text-xs uppercase tracking-wide text-white/80">Verzekerd</p>
          <p className="text-2xl font-bold mt-1">{fmt(verzekerd)}</p>
        </div>
        <div className="rounded-2xl p-5 text-white bg-gradient-to-br from-emerald-500 to-emerald-600">
          <p className="text-xs uppercase tracking-wide text-white/80">Betaald</p>
          <p className="text-2xl font-bold mt-1">{fmt(betaald)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Stamdata */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-display text-lg font-semibold text-mfb-blue mb-4">Stamgegevens</h2>
          <dl className="space-y-3 text-sm">
            <Row label="Bedrijfsnaam" value={debtor.company_name} />
            <Row label="KvK-nummer" value={debtor.company_number || '—'} />
            <Row label="Land" value={debtor.country_code} />
            <Row label="E-mail" value={debtor.contact_email || '—'} />
            <Row label="Telefoon" value={debtor.phone || '—'} />
            <Row label="Betaaltermijn" value={debtor.payment_terms_days ? `${debtor.payment_terms_days} dagen` : '—'} />
            <Row label="Status" value={debtor.status === 'active' ? 'Actief' : debtor.status === 'blocked' ? 'Geblokkeerd' : 'Inactief'} />
          </dl>
        </div>

        {/* Verzekering */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 lg:col-span-2">
          <h2 className="font-display text-lg font-semibold text-mfb-blue mb-4">Verzekerbaarheid</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="Credit grade" value={grade != null ? String(grade) : '—'} accent={gradeColor(grade)} />
            <Stat label="Max dekking" value={fmt(maxDekking)} />
            <Stat label="Verzekerbaar" value={insurable ? 'Ja' : 'Nee'} accent={insurable ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'} />
            <Stat label="Laatste toetsing" value={debtor.last_credit_check ? new Date(debtor.last_credit_check).toLocaleDateString('nl-NL') : 'Nooit'} />
          </div>
          <div className="mt-5 p-4 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-600">
            {grade == null
              ? 'Nog geen kredietcheck uitgevoerd. Voer een kredietcheck uit om de grade en dekking te bepalen.'
              : insurable
                ? `Grade ${grade}: facturen op deze debiteur zijn verzekerbaar tot maximaal ${fmt(maxDekking)} per debiteur per jaar.`
                : `Grade ${grade}: deze debiteur is niet verzekerbaar (geen dekking). Overweeg de openstaande facturen ter incasso aan te bieden.`}
          </div>
        </div>
      </div>

      {/* Saldo-opbouw */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-display text-lg font-semibold text-mfb-blue">Saldo-opbouw</h2>
          <p className="text-sm text-slate-500">Facturen op deze debiteur en de bijbehorende acties</p>
        </div>
        {invoices.length === 0 ? (
          <div className="p-12 text-center text-slate-500">Geen facturen op deze debiteur.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Factuur</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Bedrag</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Verzekerd</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Vervaldatum</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actie</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map((inv) => {
                  const canAct = inv.status === 'overdue';
                  return (
                    <tr key={inv.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm font-medium text-mfb-blue">{inv.invoice_number}</td>
                      <td className="px-6 py-4 text-sm text-slate-700">{fmt(Number(inv.amount_gross))}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{fmt(Number(inv.coverage_amount || 0))}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{new Date(inv.due_date).toLocaleDateString('nl-NL')}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLOR[inv.status] || 'bg-slate-100 text-slate-700'}`}>
                          {STATUS_LABELS[inv.status] || inv.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {canAct ? (
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => offerCollection(inv)}
                              className="px-3 py-1.5 text-xs font-medium text-mfb-blue border border-slate-300 rounded-lg hover:bg-slate-50 transition"
                            >
                              Ter incasso
                            </button>
                            <button
                              onClick={() => fileClaim(inv)}
                              disabled={busy === inv.id}
                              className="px-3 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 disabled:bg-slate-400 rounded-lg transition"
                            >
                              {busy === inv.id ? '...' : 'Schade melden'}
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <dt className="text-slate-500">{label}</dt>
    <dd className="text-slate-900 font-medium text-right">{value}</dd>
  </div>
);

const Stat: React.FC<{ label: string; value: string; accent?: string }> = ({ label, value, accent }) => (
  <div>
    <p className="text-xs text-slate-500 mb-1">{label}</p>
    {accent ? (
      <span className={`inline-block px-2.5 py-1 rounded-lg text-sm font-bold ${accent}`}>{value}</span>
    ) : (
      <p className="text-sm font-semibold text-slate-900">{value}</p>
    )}
  </div>
);

export default DebtorDetailPage;
